import { useApi } from '../Components/Store/ApiContext';

// Operations API service that follows the backend pattern
export const useOperationsApi = () => {
  const { getData, responseApi } = useApi();

  // Get daily operations (check-ins and check-outs) for a specific date
  const getDailyOperations = async (date) => {
    try {
      const response = await getData(null, `operations/dailyOperations?date=${date}`);
      return response;
    } catch (error) {
      console.error('Error fetching daily operations:', error);
      throw error;
    }
  };

  // Get all accepted requests for calendar view
  const getAllRequestsAccepted = async () => {
    try {
      const response = await getData(null, 'requests/allRequestsAccepted');
      return response;
    } catch (error) {
      console.error('Error fetching accepted requests:', error);
      throw error;
    }
  };

  // Update request status (confirm check-in/check-out)
  const updateRequestStatus = async (requestId, status, data = {}) => {
    try {
      const response = await responseApi('patch', {
        operationStatus: status,
        ...data
      }, `operations/${requestId}/status`);
      return response;
    } catch (error) {
      console.error('Error updating request status:', error);
      throw error;
    }
  };

  // Handle early/late check-in or check-out
  const handleEarlyLateOperation = async (requestId, type, requestedTime, reason, feeAmount = 0) => {
    try {
      const response = await responseApi('patch', {
        type,
        requestedTime,
        reason,
        feeAmount
      }, `operations/${requestId}/earlyLate`);
      return response;
    } catch (error) {
      console.error('Error handling early/late operation:', error);
      throw error;
    }
  };

  // Handle noShow operation
  const handleNoShowOperation = async (requestId, noShowingReason, releaseRoom = false) => {
    try {
      const response = await responseApi('patch', {
        noShowingReason,
        // releaseRoom
      }, `operations/${requestId}/noShow`);
      return response;
    } catch (error) {
      console.error('Error handling noShow operation:', error);
      throw error;
    }
  };

  // Get operations statistics for a specific date
  const getOperationsStats = async (date) => {
    try {
      const response = await getData(null, `operations/operationsStats?date=${date}`);
      return response;
    } catch (error) {
      console.error('Error fetching operations stats:', error);
      throw error;
    }
  };

  return {
    getDailyOperations,
    getAllRequestsAccepted,
    updateRequestStatus,
    handleEarlyLateOperation,
    handleNoShowOperation,
    getOperationsStats
  };
};

// Transform backend request data to frontend operations format
export const transformRequestToOperation = (request, selectedDate) => {
  const isCheckIn = new Date(request.reqFrom).toDateString() === new Date(selectedDate).toDateString();
  const isCheckOut = new Date(request.reqTo).toDateString() === new Date(selectedDate).toDateString();
  const {checkInPolicy, checkOutPolicy} = JSON.parse(sessionStorage.getItem("bookingPolicies"));
  
  return {
    id: request._id,
    type: isCheckIn ? 'checkin' : 'checkout',
    reqCode: request.reqCode,
    reqClientName: request.reqClientName,
    phone: request.client?.clientPhone || request.rentingUser?.phoneNumber || '',
    email: request.rentingUser?.email || '',
    unitCode: request.unit?.unitCode || '',
    unitName: request.unit?.unitName || '',
    expectedTime: isCheckIn ? checkInPolicy.standardTime : checkOutPolicy.standardTime,
    actualTime: request.actualTime,
    operationStatus: isCheckIn ? request.checkInStatus || 'pending' : request.checkOutStatus || 'pending', // 'pending', 'inProgress', 'completed', 'late', 'early', 'noShow'
    reqGuests: request.reqGuests || 1,
    notes: request.notes,
    reqStatus: request.reqStatus,
    reqFrom: request.reqFrom,
    reqTo: request.reqTo,
    remainingBalance: calculateRemainingBalance(request),
    roomStatus: request.roomStatus || isCheckIn ? "clean" : 'dirty',
    assignedStaff: request.createdBy?.userName,
    priority: calculatePriority(request),
    checkoutRequest : request.checkoutRequest || null,
    rawRequest: request // Keep original data for reference
  };
};

// // Map backend status to frontend status
// const mapBackendStatusToFrontend = (backendStatus) => {
//   const statusMap = {
//     'pending': 'pending',
//     'deposit': 'pending',
//     'settled': 'confirmed',
//     'rejected': 'cancelled',
//     'cancelled': 'cancelled',
//     'early': 'early',
//     'late': 'late',
//     'noShow': 'noShow'
//   };
//   return statusMap[backendStatus] || 'pending';
// };

// Calculate payment status based on payments array
const calculatePaymentStatus = (request) => {
  if (!request.reqPayments || request.reqPayments.length === 0) {
    return 'pending';
  }

  const totalPaid = request.reqPayments.reduce((sum, payment) => sum + payment.amountPaid, 0);
  const totalCost = request.reqTotalCost;

  if (totalPaid >= totalCost) {
    return 'paid';
  } else if (totalPaid > 0) {
    return 'partial';
  }
  return 'pending';
};

// Calculate remaining balance
const calculateRemainingBalance = (request) => {
  if (!request.reqPayments || request.reqPayments.length === 0) {
    return request.reqTotalCost;
  }

  const totalPaid = request.reqPayments.reduce((sum, payment) => payment.paymentType === "deposit" ? sum + payment.amountPaid : sum - payment.amountPaid, 0);
  const remaining = request.reqTotalCost - totalPaid;
  return remaining > 0 ? remaining : 0;
};

// Calculate priority based on various factors
const calculatePriority = (request) => {
  const now = new Date();
  const checkInDate = new Date(request.reqFrom);
  const checkOutDate = new Date(request.reqTo);

  // High priority for same day operations
  if (checkInDate.toDateString() === now.toDateString() ||
    checkOutDate.toDateString() === now.toDateString()) {
    return 'high';
  }

  // Urgent for overdue operations
  if ((checkInDate < now && request.reqStatus === 'pending') ||
    (checkOutDate < now && request.reqStatus === 'settled')) {
    return 'urgent';
  }

  return 'normal';
};
